/**
 * TechSheet (FT) + Label + Notification schemas.
 *
 * Invariants:
 *  - TechSheet.allergens_text is AUTO-CONSOLIDATED from the BOM (never manual). The read
 *    shape carries it; the create input does not let the client set it.
 *  - A Label is generated on OT close / packaging and NEVER from the FT directly. The read
 *    shape links a finished_lot; sales_legal (sourced from a tech sheet) is an O2 hook.
 */

import { z } from 'zod';
import {
  TECH_SHEET_STATUSES,
  LABEL_TYPES,
  NOTIFICATION_SEVERITIES,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_STATUSES,
  USER_ROLES,
} from '../enums/index.js';
import {
  uuid,
  nonEmptyString,
  timestamptz,
  nonNegativeQuantity,
  jsonObject,
  softDeleteFields,
  timestampFields,
} from './common.js';

// ── TechSheet (FT) ────────────────────────────────────────────────────────────

export const TechSheet = z
  .object({
    id: uuid,
    tenant_id: uuid,
    article_id: uuid,
    recipe_version_id: uuid.nullable(),
    version_number: z.number().int().positive(),
    legal_denomination: z.string().trim().min(1).nullable(),
    ingredients_text: z.string().nullable(),
    allergens_text: z.string().nullable(),
    net_weight: nonNegativeQuantity.nullable(),
    net_weight_unit_id: uuid.nullable(),
    conservation: z.string().trim().min(1).nullable(),
    shelf_life_days: z.number().int().nonnegative().nullable(),
    nutrition_json: jsonObject.nullable(),
    manufacturer_json: jsonObject.nullable(),
    /** Frozen copy of articles.food_safety_json — the free-text safety blocks. */
    food_safety_json: z.array(z.unknown()),
    /** Frozen copy of the tenant's legislation_notes — the sheet's closing text. */
    legislation_json: z.array(z.unknown()),
    status: z.enum(TECH_SHEET_STATUSES),
    approved_by: uuid.nullable(),
    approved_at: timestamptz.nullable(),
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type TechSheet = z.infer<typeof TechSheet>;

/**
 * Generate/update a tech sheet. The client supplies editable regulatory metadata; the
 * server derives ingredients_text (weight-descending) and allergens_text (consolidated
 * from BOM) — these are NEVER accepted from the client (.strict() rejects them).
 */
export const TechSheetInput = z
  .object({
    article_id: uuid,
    recipe_version_id: uuid.optional(),
    legal_denomination: z.string().trim().min(1).optional(),
    net_weight: nonNegativeQuantity.optional(),
    net_weight_unit_id: uuid.optional(),
    conservation: z.string().trim().min(1).optional(),
  })
  .strict();
export type TechSheetInput = z.infer<typeof TechSheetInput>;

// ── Label ─────────────────────────────────────────────────────────────────────

export const Label = z
  .object({
    id: uuid,
    tenant_id: uuid,
    label_type: z.enum(LABEL_TYPES),
    finished_lot_id: uuid.nullable(),
    tech_sheet_id: uuid.nullable(),
    label_template_id: uuid.nullable(),
    qr_payload: z.string().nullable(),
    content_json: jsonObject,
    printed_at: timestamptz.nullable(),
    printed_device_id: uuid.nullable(),
    created_at: timestamptz,
    created_by: uuid.nullable(),
  })
  .strict();
export type Label = z.infer<typeof Label>;

/**
 * Generate an internal_lot label for a finished lot (on OT close / packaging). NEVER
 * generated from the FT. The server renders content_json + qr_payload (carrying the
 * internal lot number); the client only chooses lot, template and printer.
 */
export const LabelGenerateInput = z
  .object({
    finished_lot_id: uuid,
    label_template_id: uuid.optional(),
    printed_device_id: uuid.optional(),
  })
  .strict();
export type LabelGenerateInput = z.infer<typeof LabelGenerateInput>;

// ── Notification ──────────────────────────────────────────────────────────────

export const Notification = z
  .object({
    id: uuid,
    tenant_id: uuid,
    recipient_user_id: uuid.nullable(),
    recipient_role: z.enum(USER_ROLES).nullable(),
    severity: z.enum(NOTIFICATION_SEVERITIES),
    channel: z.enum(NOTIFICATION_CHANNELS),
    title: nonEmptyString,
    body: z.string().trim().min(1).nullable(),
    target_entity_type: z.string().trim().min(1).nullable(),
    target_entity_id: uuid.nullable(),
    status: z.enum(NOTIFICATION_STATUSES),
    sent_at: timestamptz.nullable(),
    delivered_at: timestamptz.nullable(),
    read_at: timestamptz.nullable(),
    escalated_at: timestamptz.nullable(),
    created_at: timestamptz,
  })
  .strict()
  .refine((n) => n.recipient_user_id !== null || n.recipient_role !== null, {
    message: 'a notification must target a user or a role',
  });
export type Notification = z.infer<typeof Notification>;
